import { adaptFormItemResponseToFormFields } from '../core';
import {
  adaptFieldsGroupRestrictionResponse,
  adaptInternalLookupResponseToOptions,
  stampGuardedFieldError,
} from '../utilities';
import { IFormBase, ConditionalFieldsGroupFormField } from '@/forms/typings';
import {
  ConditionalFieldsGroupFormFieldBuilder,
  CheckboxGroupFormFieldBuilder,
  CheckboxFormFieldBuilder,
} from '@/forms/builders';
import {
  FormConditionalFieldsGroupResponseDTO,
  FormFieldResponseDTO,
} from '@/api/base/DTOs';
import { config } from '@/forms/config';

export const adaptFormConditionalFieldsGroupResponseToConditionalFieldsGroupFormField =
  (
    formConditionalFieldsGroupResponse: FormConditionalFieldsGroupResponseDTO,
    parentFieldId?: IFormBase['fieldId'],
  ): ConditionalFieldsGroupFormField => {
    // Build CheckboxGroup for condition field
    const conditionFieldResponse =
      formConditionalFieldsGroupResponse.conditionField as FormFieldResponseDTO;
    const isSingleChoice =
      !conditionFieldResponse.restriction?.allowMultipleValues;

    // Get selected values as a comma-separated string
    const selectedValues =
      conditionFieldResponse.values
        ?.map(v => {
          if (v.lookupID) {
            return `${config.internalLookupPrefix}${conditionFieldResponse.internalLookup?.lookupTypeID}${config.internalLookupPrefix}${v.lookupID}`;
          }
          return '';
        })
        .filter(v => v)
        .join(',') || '';

    // Build checkbox fields from internal lookup options
    const checkboxFields = conditionFieldResponse.internalLookup
      ? (
          adaptInternalLookupResponseToOptions(
            conditionFieldResponse.internalLookup,
            conditionFieldResponse.values,
          ) || []
        ).map(option => {
          const optionValue = String(option.value);
          return new CheckboxFormFieldBuilder()
            .fieldId(optionValue)
            .name(optionValue)
            .label(option.label)
            .value(
              selectedValues !== '' && selectedValues.includes(optionValue),
            )
            .hasAnyValue(
              selectedValues !== '' && selectedValues.includes(optionValue),
            )
            .notApplicable(false)
            .showNotApplicable(false)
            .size(12)
            .readonly(
              conditionFieldResponse.readonly ||
                !conditionFieldResponse.isEditable,
            )
            .build();
        })
      : [];

    const conditionFieldBuilder = new CheckboxGroupFormFieldBuilder()
      .fieldId(conditionFieldResponse.fieldID)
      .name(conditionFieldResponse.fieldID)
      .label(conditionFieldResponse.label ?? conditionFieldResponse.name)
      .fields(checkboxFields)
      .value(selectedValues)
      .singleChoice(isSingleChoice)
      .size(conditionFieldResponse.itemSize)
      .scale('medium')
      .hasAnyValue(conditionFieldResponse.hasAnyValue)
      .notApplicable(conditionFieldResponse.notApplicable)
      .showNotApplicable(conditionFieldResponse.showNotApplicable)
      .parentFieldId(formConditionalFieldsGroupResponse.fieldID);

    if (conditionFieldResponse.hint) {
      conditionFieldBuilder.hint(conditionFieldResponse.hint);
    }

    if (conditionFieldResponse.hintImageUrl) {
      conditionFieldBuilder.hintImageUrl(conditionFieldResponse.hintImageUrl);
    }

    const conditionField = conditionFieldBuilder.build();

    let conditionalFieldsGroupField =
      new ConditionalFieldsGroupFormFieldBuilder()
        .fieldId(formConditionalFieldsGroupResponse.fieldID)
        .name(formConditionalFieldsGroupResponse.title)
        .conditionField(conditionField)
        .children(
          formConditionalFieldsGroupResponse.children.map(child =>
            adaptFormItemResponseToFormFields(
              child,
              formConditionalFieldsGroupResponse.fieldID,
            ),
          ),
        )
        .hasAnyValue(formConditionalFieldsGroupResponse.hasAnyValue)
        .notApplicable(formConditionalFieldsGroupResponse.notApplicable)
        .showNotApplicable(formConditionalFieldsGroupResponse.showNotApplicable)
        .isRequired(formConditionalFieldsGroupResponse.isRequired)
        .parentFieldId(parentFieldId);

    if (formConditionalFieldsGroupResponse.hint) {
      conditionalFieldsGroupField = conditionalFieldsGroupField.hint(
        formConditionalFieldsGroupResponse.hint,
      );
    }

    if (formConditionalFieldsGroupResponse.hintImageUrl) {
      conditionalFieldsGroupField = conditionalFieldsGroupField.hintImageUrl(
        formConditionalFieldsGroupResponse.hintImageUrl,
      );
    }

    const groupRestrictions = formConditionalFieldsGroupResponse.groupRestrictions
      ?.filter(gr => gr.restriction.atLeastOneChildWithValue)
      .map(gr => ({
        groupFieldId: gr.groupFieldID,
        restriction: adaptFieldsGroupRestrictionResponse(gr.restriction)!,
      }));

    if (groupRestrictions?.length) {
      conditionalFieldsGroupField =
        conditionalFieldsGroupField.groupRestrictions(groupRestrictions);
    }

    return stampGuardedFieldError(conditionalFieldsGroupField.build());
  };
